import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as http from "../../utils/http";
import { useCartContext } from "../../customHooks/useCart";
import { loadCustomerFromSession } from "../../customHooks/useCustomer";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import { getMedicationBrandFromTitle } from "../../utils/wlFunnelExperiment";
import { CarePlusMedication, CarePlusDosageOption } from "./types";
import { DisplayedPriceComponent, displayedMedicationPrice, toChargeCurrency } from "../../utils/helpers";
import { CatalogSettingContext } from "../ChooseTreatmentVariants/chooseTreatmentContexts";

// HL-9781 §1: Choose Treatment (Care+) data + selection state.
// Catalog/country-agnostic: everything is driven by the route's mention/country/language.
// Reuses the existing, already-generic choose-treatment-free-dosage endpoint.
//
// IMPORTANT data model: the endpoint returns one entry PER STRENGTH (each product_id is a
// dosage strength, e.g. Mounjaro 2.5mg / 5mg / 7.5mg), all sharing the same catalog_id +
// title. The Care+ UI treats a *catalog* as one "medication" and its strengths as the
// dosage options. So we group products by catalog_id, and each strength becomes a dosage.

interface UseChooseTreatmentCarePlus {
  loading: boolean;
  error: boolean;
  medications: CarePlusMedication[]; // full list, original order — recommended sits wherever it naturally falls
  recommendedCatalogId: number | null;
  // HL-11657: the campaign catalog to promote (recommend + open its card + list it first), or null.
  // This is the URL ?catalog_id= only for the A/B test's variant B; variant A and non-enrolled
  // visitors get null, so they see the default order with all cards closed (AC-3/AC-7).
  // Known at mount (no fetch needed), so the mobile accordion can open that card immediately (AC-8).
  recommended: CarePlusMedication | null;
  alternatives: CarePlusMedication[];
  selectedDosage: CarePlusDosageOption | null;
  setSelectedDosageId: (productItemId: number) => void;
  selectRecommended: (catalogId: number) => void; // swap an alternative into the recommended slot
  continueWithMedication: () => void; // -> in-page plan, or shipping for pre-order
  openPlanStep: () => void; // HL-12777: show the plan step for the selection on screen (Continue, browser Forward)
  goToPlan: boolean;
  setGoToPlan: (value: boolean) => void; // true when the CTA has been clicked and the page should navigate to Choose Plan
  submitting: boolean;
  showLoginModal: boolean;
  closeLoginModal: () => void;
  onLoginSuccess: () => void;
}

// Parse the leading numeric value out of a dosage label ("2,5 mg" -> 2.5) for sorting.
const dosageValue = (dosageStr: string): number => {
  const match = (dosageStr || "").replace(",", ".").match(/[\d.]+/);
  return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
};

// Extract a dosage label from a product title when dosage_str is not set.
// "Mounjaro® KwikPen 2,5 mg Injektionslösung" → "2,5 mg"
const extractDosageFromTitle = (title: string): string => {
  const match = (title || "").match(/[\d][0-9,.]* mg/i);
  return match ? match[0] : "";
};

// Prefer an in-stock dosage; for pre-order catalogs every pack is choosable even when OOS.
export const firstChoosableDosage = (
  dosages: CarePlusDosageOption[],
  isPreOrderable?: boolean,
): CarePlusDosageOption | null => {
  if (!dosages.length) return null;
  if (isPreOrderable) return dosages[0] ?? null;
  return dosages.find((d) => d.in_stock) ?? dosages[0] ?? null;
};

// A strength's `items` are its pack sizes (1 pen / 3 pens / 5 pens ...), each with its own
// product_item_id, price and quantity. Every pack is a selectable option, so the whole list is
// kept — collapsing to a single "representative" item silently hid the larger packs and made the
// pen count disagree with the price shown next to it.

// Group raw choose-treatment products (one per strength) into medications (one per catalog).
const toMedications = (products: any[], displayedPriceComponents: DisplayedPriceComponent[] | null = null): CarePlusMedication[] => {
  const byCatalog = new Map<number, any[]>();
  for (const product of products) {
    const catalogId = product.catalog_id;
    if (catalogId == null) continue;
    if (!byCatalog.has(catalogId)) byCatalog.set(catalogId, []);
    byCatalog.get(catalogId)!.push(product);
  }

  const medications: CarePlusMedication[] = [];
  for (const [catalogId, group] of byCatalog) {
    const head = group[0];
    // Catalog-level flag from formatChooseTreatmentProducts (settings.is_pre_orderable).
    const isPreOrderable = group.some((p) => Boolean(p.is_pre_orderable));

    // One option per (strength x pack size). in_stock is per item: a strength can have the 1-pen
    // pack in stock and the 5-pen pack out, so it must not be inherited from the strength.
    const dosages: CarePlusDosageOption[] = group
      .flatMap((strength) => {
        const dosageStr =
          strength.dosage_str || extractDosageFromTitle(strength.product_title) || "";
        return (strength.items ?? []).map(
          (item: any) =>
            ({
              product_item_id: item.product_item_id,
              // Prefer dosage_str, then parse the mg value from product_title, then fall back.
              dosage_str: dosageStr || item.custom_quantity_title || "",
              // HL-11158: never use item.price (it also folds in treatment + shipping).
              // Catalog setting overrides the default when present.
              // HL-11357: the Care+ default is the bare medication price. Care+ itemises the
              // service fee on the next screen (it lives on the plan, not the product), so
              // folding it in here would show a higher number than Choose Plan for the same item.
              price: displayedPriceComponents
                ? displayedMedicationPrice(item, displayedPriceComponents)
                : Number(item.official_price ?? item.price ?? 0),
              official_price: Number(item.official_price ?? item.price ?? 0),
              in_stock: Boolean(item.in_stock),
              quantity: Number(item.quantity ?? 1),
              quantity_title: item.custom_quantity_title || "",
            } as CarePlusDosageOption),
        );
      })
      // Ascending by strength, then by pack size within the same strength.
      .sort(
        (a, b) =>
          dosageValue(a.dosage_str) - dosageValue(b.dosage_str) || a.quantity - b.quantity,
      );

    if (!dosages.length) continue;

    medications.push({
      product_id: head.product_id, // representative strength of the catalog
      catalog_id: catalogId,
      title: head.title,
      description: head.description ?? "",
      short_description: head.short_description ?? "",
      image: head.image ?? null,
      focus_image: head.focus_image ?? null,
      social_proof: head.social_proof ?? null,
      in_stock: group.some((p) => p.in_stock),
      is_pre_orderable: isPreOrderable,
      treatments_order: Number(head.treatments_order ?? Number.POSITIVE_INFINITY),
      dosages,
    });
  }
  return medications;
};

// SSO / guest resume marker — same idea as pendingCarePlusCheckout on Choose Plan.
// Stores the selected catalog + dosage so a full-page OAuth redirect can restore them.
const PENDING_PRESALE_KEY = "pendingCarePlusPresaleCheckout";

type PendingPresale = {
  catalogId: number;
  productItemId: number;
};

const readPendingPresale = (): PendingPresale | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_PRESALE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.catalogId === "number" &&
      typeof parsed.productItemId === "number"
    ) {
      return parsed as PendingPresale;
    }
  } catch {}
  return null;
};

const writePendingPresale = (pending: PendingPresale) => {
  try {
    sessionStorage.setItem(PENDING_PRESALE_KEY, JSON.stringify(pending));
  } catch {}
};

const clearPendingPresale = () => {
  try {
    sessionStorage.removeItem(PENDING_PRESALE_KEY);
  } catch {}
};

// HL-12777: the medication + dose the customer continued to Choose Plan with. Back from Choose Plan
// keeps it in memory; this copy covers the remounts (reload or browser Forward on /choose_plan).
// `link` is the ?catalog_id= / ?promote= it was picked under: arriving through a different link
// drops it, so a new campaign still opens on its own product.
const PICKED_TREATMENT_KEY = "carePlusPickedTreatment";

// HL-12777: marks the history entry the plan step pushes, so the header arrow pops it like browser Back.
export const PLAN_STEP_HISTORY_KEY = "carePlusPlanStep";

type PickedTreatment = {
  category: string;
  link: string;
  catalogId: number;
  productItemId: number;
};

const linkOf = (searchParams: URLSearchParams): string =>
  `${searchParams.get("catalog_id") ?? ""}|${searchParams.get("promote") ?? ""}`;

const readPickedTreatment = (): PickedTreatment | null => {
  try {
    const raw = sessionStorage.getItem(PICKED_TREATMENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.category === "string" &&
      typeof parsed.link === "string" &&
      typeof parsed.catalogId === "number" &&
      typeof parsed.productItemId === "number"
    ) {
      return parsed as PickedTreatment;
    }
  } catch {}
  return null;
};

const writePickedTreatment = (picked: PickedTreatment) => {
  try {
    sessionStorage.setItem(PICKED_TREATMENT_KEY, JSON.stringify(picked));
  } catch {}
};

const clearPickedTreatment = () => {
  try {
    sessionStorage.removeItem(PICKED_TREATMENT_KEY);
  } catch {}
};

/** `?catalog_id=` as a positive integer, or null when absent/garbage. */
const parseCatalogId = (raw: string | null): number | null => {
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// HL-11360: the V3 page renders Choose Plan in place instead of routing to it (see `goToPlan`).
// The campaign / product-page ?catalog_id= always opens as the top product (HL-12595: the HL-11657
// A/B test is closed) and the other medications stay listed below it.
// `preferCart` (V3 plan-step reopen): the product already in the cart outranks ?catalog_id=.
// HL-12777: the customer's own pick outranks both, and a returning customer's last purchase comes
// before the default (which the default-product A/B test may have swapped in).
export const useChooseTreatmentCarePlus = ({
  preferCart = false,
  ctPreselectedCatalogId,
}: { preferCart?: boolean; ctPreselectedCatalogId?: number | null } = {}): UseChooseTreatmentCarePlus => {
  const { country, language, category } = useParams() as { country: string; language: string; category: string };
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getCart, deactivateSubscriptionInDB, cartCurrency } = useCartContext();
  const catalogSettings = useContext(CatalogSettingContext) as {
    displayed_price_components?: DisplayedPriceComponent[] | null;
    promoted_catalog_id?: number | null;
    last_purchased_product_item_ids?: number[];
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [medications, setMedications] = useState<CarePlusMedication[]>([]);
  const [recommendedCatalogId, setRecommendedCatalogId] = useState<number | null>(null);
  const [selectedDosageId, setSelectedDosageId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [goToPlan, setGoToPlan] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  // Survives email/password login; SSO uses sessionStorage (full-page redirect).
  const pendingPresaleCheckout = useRef(false);
  const resumedAfterOAuth = useRef(false);

  // Read once per navigation: preselection is a mount-time concern, and the
  // fetch effect below deliberately does not re-run when the query string changes.
  const urlCatalogId = parseCatalogId(searchParams.get("catalog_id"));

  useEffect(() => {
    http
      .getFromServer(`choose-treatment-free-dosage/${category}/${country}/${language}`, [
        http.MIDDLEWARES.CUSTOMER_ID,
        http.MIDDLEWARES.CART_ORDER,
      ])
      .then((response) => {
        const products: any[] = response?.data?.products ?? [];
        // Care+ is not under ReturningCustomersLayoutContext, so read settings from the router context.
        const allMeds = toMedications(products, catalogSettings?.displayed_price_components ?? null);

        // Prefer a pending presale selection (SSO resume) over url / cart / default.
        const pending = readPendingPresale();

        // ?promote=<product> (resolved by the settings GET): that product moves to position #1 and
        // is the recommended (expanded) one; the alternatives stay listed.
        const promoteCatalogId = catalogSettings?.promoted_catalog_id ?? null;
        const promoteMed = promoteCatalogId ? allMeds.find((m) => m.catalog_id === promoteCatalogId) : undefined;
        const meds = promoteMed ? [promoteMed, ...allMeds.filter((m) => m !== promoteMed)] : allMeds;
        setMedications(meds);
        const fromPending = pending
          ? meds.find((m) => m.catalog_id === pending.catalogId)
          : undefined;

        // Campaign and product-page links carry the advertised medication as
        // ?catalog_id=, forwarded through the questionnaire — same preselection
        // ReturningCustomersLayout does. It outranks the cart so a link always
        // opens on the medication it promised, but never overrides a pending
        // presale, which is the customer's own explicit earlier choice.
        // An id belonging to another mention simply matches nothing and falls through.
        const fromUrl = urlCatalogId
          ? meds.find((m) => m.catalog_id === urlCatalogId)
          : undefined;

        // HL-12777: the pick only counts under the link it was made with; a new link drops it.
        const storedPick = readPickedTreatment();
        const picked =
          storedPick && storedPick.category === category && storedPick.link === linkOf(searchParams)
            ? storedPick
            : null;
        if (storedPick && !picked) clearPickedTreatment();
        const fromPicked = picked ? meds.find((m) => m.catalog_id === picked.catalogId) : undefined;

        // Recommended = pending presale → customer's pick → url catalog_id → cart.product_item_id →
        // last purchase (returning customers) → ctPreselectedCatalogId → first catalog medication.
        // `meds` arrives already sorted by catalogs.treatments_order (ProductsModel), so meds[0] is the
        // lowest-order medication.
        const cart = getCart();
        const cartItemId = (cart as any)?.product_item_id;
        const fromCart = cartItemId
          ? meds.find((m) => m.dosages.some((d) => d.product_item_id === cartItemId))
          : undefined;
        // The last paid WL order can hold several products: the first one still on the page wins.
        const lastPurchasedItemId =
          (catalogSettings?.last_purchased_product_item_ids ?? []).find((itemId) =>
            meds.some((m) => m.dosages.some((d) => d.product_item_id === itemId)),
          ) ?? null;
        const fromLastPurchase = lastPurchasedItemId
          ? meds.find((m) => m.dosages.some((d) => d.product_item_id === lastPurchasedItemId))
          : undefined;
        const fromLink = fromUrl ?? promoteMed;
        // Back from Shipping / reload reopens the plan step for what the customer put in the cart.
        const fromDefault = meds.find((m) => m.catalog_id === ctPreselectedCatalogId);
        const initial =
          fromPending ??
          fromPicked ??
          (preferCart ? fromCart ?? fromLink : fromLink ?? fromCart) ??
          fromLastPurchase ??
          fromDefault ??
          meds[0] ??
          null;

        setRecommendedCatalogId(initial?.catalog_id ?? null);
        // Preselect pending dosage, else the picked one, else cart strength, else the last purchase,
        // else first choosable. Each only matches when `initial` is that medication.
        const fromPendingDosage = pending
          ? initial?.dosages.find((d) => d.product_item_id === pending.productItemId)
          : undefined;
        const fromPickedDosage = picked
          ? initial?.dosages.find((d) => d.product_item_id === picked.productItemId)
          : undefined;
        const fromCartDosage = initial?.dosages.find((d) => d.product_item_id === cartItemId);
        const fromLastPurchaseDosage = lastPurchasedItemId
          ? initial?.dosages.find((d) => d.product_item_id === lastPurchasedItemId)
          : undefined;
        const firstChoosable = firstChoosableDosage(initial?.dosages ?? [], initial?.is_pre_orderable);
        setSelectedDosageId(
          (fromPendingDosage ?? fromPickedDosage ?? fromCartDosage ?? fromLastPurchaseDosage ?? firstChoosable)
            ?.product_item_id ?? null,
        );
        // CT page view is owned by ChooseTreatmentRouter only — do not fire it here
        // (same event name was double-counting Care+ and ~100% Q→CT conversion).
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [category, country, language, catalogSettings?.displayed_price_components]);

  const recommended = useMemo(
    () => medications.find((m) => m.catalog_id === recommendedCatalogId) ?? null,
    [medications, recommendedCatalogId],
  );
  const alternatives = useMemo(
    () => medications.filter((m) => m.catalog_id !== recommendedCatalogId),
    [medications, recommendedCatalogId],
  );
  const selectedDosage = useMemo(
    () => recommended?.dosages.find((d) => d.product_item_id === selectedDosageId) ?? null,
    [recommended, selectedDosageId],
  );

  // §8.1: selecting an alternative swaps it into the recommended slot and resets the dosage.
  const selectRecommended = (catalogId: number) => {
    const med = medications.find((m) => m.catalog_id === catalogId);
    if (!med) return;
    setRecommendedCatalogId(catalogId);
    const firstChoosable = firstChoosableDosage(med.dosages, med.is_pre_orderable);
    setSelectedDosageId(firstChoosable?.product_item_id ?? null);
  };

  // Presale only: every other selection continues to the in-page Choose Plan.
  const navigateToShipping = () => {
    // Care+ shipping chrome without a plan fee (same marker as Choose Plan one-time).
    const params = new URLSearchParams(searchParams);
    navigate({ pathname: `/shipping/${country}/${language}`, search: params.toString() });
  };

  // HL-12777: show the plan step for the selection on screen and keep it as the pick, so a reload on
  // /choose_plan reopens the same medication + dose.
  const openPlanStep = () => {
    if (!selectedDosage || !recommended) return;
    writePickedTreatment({
      category,
      link: linkOf(searchParams),
      catalogId: recommended.catalog_id,
      productItemId: selectedDosage.product_item_id,
    });
    setGoToPlan(true);
  };

  // §1.5: hand the selection to the in-page Choose Plan or, for pre-orderable catalogs (HL-11248),
  // persist it to the cart and go to shipping — no subscription / choose plan for presale.
  const continueWithMedication = () => {
    if (!selectedDosage || !recommended) return;

    const isPreOrder = Boolean(recommended.is_pre_orderable);

    // HL-12122: one shared event for every funnel version (split by funnel_variant); each dose is its own product.
    MainTracker.track("custom", "treatment_selected", {
      mention: category,
      catalog_id: recommended.catalog_id,
      product_id: selectedDosage.product_item_id,
      product_name: `${recommended.title} ${selectedDosage.dosage_str}`.trim(),
      medication_brand: getMedicationBrandFromTitle(recommended.title),
      medication_price: toChargeCurrency(selectedDosage.official_price, cartCurrency?.rate, country),
    });

    // HL-11360: Choose Plan renders in place. Fake the URL so the address bar and
    // header back read as Choose Plan, hand the selection over in memory, and stop — the plan
    // page writes product and plan to the cart together on its own Continue.
    if (!isPreOrder) {
      // HL-12777: push (not replace) so browser Back returns to this page; the page closes the plan
      // step on popstate and reopens it on Forward.
      const params = window.location.search;
      window.history.pushState(
        { ...window.history.state, [PLAN_STEP_HISTORY_KEY]: true },
        "",
        `/choose_plan/${country}/${language}/${category}${params}`,
      );
      openPlanStep();
      return;
    }

    // HL-11248: shipping + deactivateSubscription need an authenticated customer.
    // Mirror Choose Plan's guest gate so we never 401 mid-continue.
    if (isPreOrder && !loadCustomerFromSession()) {
      pendingPresaleCheckout.current = true;
      writePendingPresale({
        catalogId: recommended.catalog_id,
        productItemId: selectedDosage.product_item_id,
      });
      setShowLoginModal(true);
      return;
    }

    setSubmitting(true);

    // Persist via choose-treatment-free-dosage (same writer free-dosage CT uses).
    // Do NOT use addProductItemsToCart: its /cart/products/update/quantity call is
    // debounced, so await settles before the HTTP write and shipping opened empty.
    //
    // Pre-order (HL-11248): same rule as ReturningCustomersLayout —
    // OOS + is_pre_orderable is NOT prescription-only. Rx-only would hide the
    // medication line on Care+ shipping (ShippingV2: !cart.only_prescription).
    // Flow: CT → shipping, skip choose plan. product_in_stock stays true so the
    // cart is treated as a normal one-time med purchase; catalog is_pre_orderable
    // drives pre-order delivery chrome.
    const isPrescriptionOnly = !selectedDosage.in_stock && !isPreOrder;
    const request = http
      .postToServer(
        "choose-treatment-free-dosage",
        {
          cartProductsDTOs: [
            {
              quantity: 1,
              product_in_stock: !isPrescriptionOnly,
              only_prescription: isPrescriptionOnly,
              catalog_id: recommended.catalog_id,
              product_item_id: selectedDosage.product_item_id,
              is_pre_selected: false,
            },
          ],
        },
        [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER],
      )
      // Presale = one-time purchase: clear any leftover Care+ plan on the cart.
      .then(() => (isPreOrder ? deactivateSubscriptionInDB() : undefined));

    // A 401 mid-request clears the session and reloads the page without ever settling this
    // promise (see http.js handlerError), which would otherwise leave the CTA silently stuck.
    // Only navigate on success — navigating after a failed/empty cart write hits shipping
    // with no products (no_products_in_cart). Timeout only unsticks the button.
    let settled = false;
    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true;
        setSubmitting(false);
      }
    }, 8000);
    request
      .then(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        navigateToShipping();
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        setSubmitting(false);
      });
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
  };

  // Email/password login: selection is still in React state — clear the pending marker
  // and retry the same continue the guest clicked.
  const onLoginSuccess = () => {
    setShowLoginModal(false);
    pendingPresaleCheckout.current = false;
    clearPendingPresale();
    continueWithMedication();
  };

  // Resume after Google/Apple SSO round-trip (page remounts authenticated with pending marker).
  // Products GET restores catalog/dosage from sessionStorage before this runs.
  useEffect(() => {
    if (resumedAfterOAuth.current || loading || error || !medications.length) return;
    if (!readPendingPresale() && !pendingPresaleCheckout.current) return;
    if (!loadCustomerFromSession()) return;
    // Wait until the restored selection is applied to state.
    if (!selectedDosage || !recommended?.is_pre_orderable) return;

    resumedAfterOAuth.current = true;
    pendingPresaleCheckout.current = false;
    clearPendingPresale();
    continueWithMedication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, medications, selectedDosage, recommended]);

  return {
    loading,
    error,
    medications,
    recommendedCatalogId,
    recommended,
    alternatives,
    selectedDosage,
    setSelectedDosageId,
    selectRecommended,
    continueWithMedication,
    openPlanStep,
    goToPlan,
    setGoToPlan,
    submitting,
    showLoginModal,
    closeLoginModal,
    onLoginSuccess,
  };
};
